import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IOption, IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import {
  CANCEL_SUBSCRIPTION,
  GET_ALL_FIRM_PURCHASED_SUBSCRIPTION,
  GET_PAYMENT_ORDER_DOCUMENT,
  GLOBAL_DROP_DOWN,
} from "../../services/Endpoints";
import { IFirmPurchasedSubscriptionFilter } from "../../services/firm-purchased-subscription/FirmPurchasedSubscriptionService";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import { saveSignedDocument } from "../../utils/FileDownload";
import { EXPIRY_WARNING_DAYS, getPlanTypeLabel } from "../../utils/PurchaseConstants";
import {
  DOCUMENT_TYPE_ENUM,
  SUBSCRIPTION_END_REASON_ENUM,
  SUBSCRIPTION_SOURCE_ENUM,
} from "../../utils/PurchaseEnumerations";
import { formatAmountWithSign, formatPurchaseDate } from "../../utils/PurchaseFormat";

interface IFirmPurchasedSubscriptionApi {
  id: string;
  firm_id: string;
  firm_name: string;
  subscription_package_name: string;
  platforms: string[] | null;
  source: SUBSCRIPTION_SOURCE_ENUM;
  end_reason: SUBSCRIPTION_END_REASON_ENUM | null;
  order_id: string | null;
  invoice_number: string | null;
  receipt_number: string | null;
  subscription_date: string;
  start_date: string;
  end_date: string;
  remaining_expiry_days: string;
  total: string;
}

export interface IFirmPurchasedSubscription
  extends Omit<IFirmPurchasedSubscriptionApi, "remaining_expiry_days"> {
  days_left: number;
}

export const FIRM_PURCHASED_SUBSCRIPTION_FILTER_INITIAL_VALUE: IFirmPurchasedSubscriptionFilter = {
  firmId: null,
  showHistoricalRecords: BIT_VALUE.Zero,
  source: null,
};

interface IFirmPurchasedSubscriptionCells {
  action: (row: IFirmPurchasedSubscription) => JSX.Element;
  source: (row: IFirmPurchasedSubscription) => JSX.Element;
  endDate: (row: IFirmPurchasedSubscription) => JSX.Element;
}

const toRow = ({
  remaining_expiry_days,
  ...item
}: IFirmPurchasedSubscriptionApi): IFirmPurchasedSubscription => ({
  ...item,
  days_left: Number(remaining_expiry_days) || 0,
});

export const isRunningSubscription = (item: IFirmPurchasedSubscription) =>
  item.days_left > 0 && !item.end_reason;

export const getSubscriptionRowClass = (item: IFirmPurchasedSubscription) => {
  if (item.end_reason === SUBSCRIPTION_END_REASON_ENUM.Replaced) return undefined;
  if (item.days_left <= 0) return "bg-danger-muted";
  if (item.days_left <= EXPIRY_WARNING_DAYS) return "bg-warning-muted";
  return undefined;
};

export const getFirmPurchasedSubscriptionFields = (
  cells: IFirmPurchasedSubscriptionCells,
  t: TUseTranslationTfn,
): ITableFields<IFirmPurchasedSubscription>[] => [
  {
    name: t("firm_name"),
    key: "firm_name",
    sort: false,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
  },
  {
    name: t("package"),
    key: "subscription_package_name",
    sort: false,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
  },
  {
    name: t("plan_type"),
    key: "platforms",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) =>
      data.platforms ? getPlanTypeLabel(t, data.platforms) : "-",
  },
  {
    name: t("source"),
    key: "source",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-3",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) => cells.source(data),
  },
  {
    name: t("start_date"),
    key: "start_date",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) =>
      formatPurchaseDate(data.start_date),
  },
  {
    name: t("end_date"),
    key: "end_date",
    sort: true,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) => cells.endDate(data),
  },
  {
    name: t("amount"),
    key: "total",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.right,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) =>
      data.source === SUBSCRIPTION_SOURCE_ENUM.Purchase ? formatAmountWithSign(data.total) : "-",
  },
  {
    name: t("days"),
    key: "days_left",
    sort: false,
    type: TABLE_FIELD_TYPE.text,
    align: TABLE_FIELD_ALIGN.center,
    flex: "col-span-1",
  },
  {
    name: t("invoice_no"),
    key: "invoice_number",
    sort: false,
    type: TABLE_FIELD_TYPE.format,
    align: TABLE_FIELD_ALIGN.left,
    flex: "col-span-2",
    format: ({ data }: ITabelFieldViewProps<IFirmPurchasedSubscription>) =>
      data.invoice_number ?? "-",
  },
  {
    name: t("action"),
    key: "action",
    sort: false,
    type: TABLE_FIELD_TYPE.custom,
    align: TABLE_FIELD_ALIGN.center,
    flex: "col-span-2",
    component: cells.action,
  },
];

export const getAllFirmPurchasedSubscription = async (
  setList: TReactSetState<IFirmPurchasedSubscription[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  filter: IFirmPurchasedSubscriptionFilter,
) => {
  try {
    const response = await GET_ALL_FIRM_PURCHASED_SUBSCRIPTION(pagination, search, filter);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS && response.data) {
      const items: IFirmPurchasedSubscriptionApi[] = response.data.items ?? [];
      setList(items.map(toRow));
      setPagination(response.data.pagination);
      return;
    }
    setList([]);
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "getAllFirmPurchasedSubscription");
  }
};

export const getFirmNameDdl = async (
  setFirmName: TReactSetState<IOption<string>[]>,
  includeDeletedFirm: boolean = true,
) => {
  try {
    const response = await GLOBAL_DROP_DOWN({
      entity: "firms",
      data_value: "id",
      display_value: "firm_name",
      ...(includeDeletedFirm && {
        filters: [{ field: "is_deleted", value: "1|0", condition: "eq" }],
        additional_field: ["is_deleted"],
      }),
    });
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setFirmName(response.data ?? []);
      return;
    }
    toastError(response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } catch (error) {
    handleError(error, "getFirmNameDdl");
  }
};

export const cancelFirmSubscription = async (id: string) => {
  try {
    const response = await CANCEL_SUBSCRIPTION(id);
    if (response?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: response.message });
      return true;
    }
    sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
  } catch (error) {
    handleError(error, "cancelFirmSubscription");
  }
  return false;
};

export const downloadOrderDocument = async (
  orderId: string,
  documentType: DOCUMENT_TYPE_ENUM,
) => {
  try {
    const response = await GET_PAYMENT_ORDER_DOCUMENT(orderId, documentType);
    if (response?.code !== DEFAULT_STATUS_CODE_SUCCESS || !response.data?.url) {
      sweetAlertError({ message: response?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
      return;
    }
    if (!(await saveSignedDocument(response.data))) {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
    }
  } catch (error) {
    handleError(error, "downloadOrderDocument");
  }
};
